import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { leaveAPI, shiftChangeAPI, employeeAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { getInitials, formatDate } from '../../lib/utils';
import { Calendar, Clock, Check, X, Loader2 } from 'lucide-react';

const ApprovalQueue = () => {
  const { user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [shiftChanges, setShiftChanges] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const [leavesRes, shiftsRes, empRes] = await Promise.all([
        leaveAPI.getRequests(user.company_id, 'pending'),
        shiftChangeAPI.getAll(user.company_id, 'pending'),
        employeeAPI.getAll(user.company_id)
      ]);
      setLeaveRequests(leavesRes.data);
      setShiftChanges(shiftsRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getEmployee = (empId) => {
    return employees.find(e => e.id === empId) || {};
  };

  const handleApproveLeave = async (id) => {
    setProcessing(id);
    try {
      await leaveAPI.approve(id);
      toast.success('Leave approved');
      fetchData();
    } catch (err) {
      toast.error('Failed to approve leave');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectLeave = async (id) => {
    setProcessing(id);
    try {
      await leaveAPI.reject(id, '');
      toast.success('Leave rejected');
      fetchData();
    } catch (err) {
      toast.error('Failed to reject leave');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveShiftChange = async (id) => {
    setProcessing(id);
    try {
      await shiftChangeAPI.approve(id);
      toast.success('Shift change approved');
      fetchData();
    } catch (err) {
      toast.error('Failed to approve shift change');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Approval Queue">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const totalPending = leaveRequests.length + shiftChanges.length;

  return (
    <DashboardLayout 
      title="Approval Queue" 
      subtitle={`${totalPending} pending request${totalPending !== 1 ? 's' : ''}`}
    >
      <Tabs defaultValue="leaves" className="space-y-6">
        <TabsList>
          <TabsTrigger value="leaves" className="gap-2">
            <Calendar className="h-4 w-4" />
            Leave Requests
            {leaveRequests.length > 0 && (
              <Badge variant="destructive">{leaveRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="shifts" className="gap-2">
            <Clock className="h-4 w-4" />
            Shift Changes
            {shiftChanges.length > 0 && (
              <Badge variant="destructive">{shiftChanges.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leaves" className="space-y-4">
          {leaveRequests.length === 0 ? (
            <Card className="p-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No pending leave requests</p>
            </Card>
          ) : (
            leaveRequests.map((leave) => {
              const emp = getEmployee(leave.employee_id);
              return (
                <Card key={leave.id} data-testid={`leave-request-${leave.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={emp.avatar_url} />
                          <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold">{emp.first_name} {emp.last_name}</p>
                          <p className="text-sm text-muted-foreground">{emp.email}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge variant="outline">
                              {leave.start_date} to {leave.end_date}
                            </Badge>
                          </div>
                          <p className="text-sm mt-2">
                            <span className="text-muted-foreground">Reason: </span>
                            {leave.reason}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleRejectLeave(leave.id)}
                          disabled={processing === leave.id}
                        >
                          {processing === leave.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <X className="h-4 w-4 mr-1" />
                              Reject
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApproveLeave(leave.id)}
                          disabled={processing === leave.id}
                        >
                          {processing === leave.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="shifts" className="space-y-4">
          {shiftChanges.length === 0 ? (
            <Card className="p-12 text-center">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No pending shift change requests</p>
            </Card>
          ) : (
            shiftChanges.map((change) => {
              const emp = getEmployee(change.employee_id);
              return (
                <Card key={change.id} data-testid={`shift-change-${change.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={emp.avatar_url} />
                          <AvatarFallback>{getInitials(emp.first_name, emp.last_name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold">{emp.first_name} {emp.last_name}</p>
                          <p className="text-sm text-muted-foreground">{emp.email}</p>
                          <Badge variant="outline" className="mt-2">
                            Date: {change.date}
                          </Badge>
                          <p className="text-sm mt-2">
                            <span className="text-muted-foreground">Reason: </span>
                            {change.reason}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleApproveShiftChange(change.id)}
                        disabled={processing === change.id}
                      >
                        {processing === change.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default ApprovalQueue;
